import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1000, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// ✅ CLIENT-SIDE IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error('Canvas is empty')); return; }
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '') + '.jpg', { type: 'image/jpeg' });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default function ScienceQuestionUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState('1');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '', details: null });
  
  const [questionsNeedingImages, setQuestionsNeedingImages] = useState([]);
  const [imageUploadStatus, setImageUploadStatus] = useState({});

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const handleUploadCSV = async (e) => {
    e.preventDefault();
    if (!file || !level) { setStatus({ type: 'error', message: 'Please select a CSV file and choose a level.' }); return; }

    setUploading(true);
    setStatus({ type: '', message: '', details: null });
    setQuestionsNeedingImages([]);
    
    const formData = new FormData();
    formData.append('csvFile', file);
    formData.append('level', level);

    try {
      const res = await fetch(`${API_BASE}/admin/upload/science-questions`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      
      let message = `✅ Processed ${data.total_processed} questions:`;
      if (data.inserted > 0) message += ` ${data.inserted} new added`;
      if (data.duplicates_skipped > 0) message += `, ${data.duplicates_skipped} duplicates skipped`;
      if (data.errors && data.errors.length > 0) message += `, ${data.errors.length} errors`;
      
      setStatus({ type: 'success', message, details: data });
      
      if (data.questions_needing_images && data.questions_needing_images.length > 0) {
        setQuestionsNeedingImages(data.questions_needing_images);
      } else {
        setFile(null);
      }
    } catch (err) {
      setStatus({ type: 'error', message: `❌ ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  const handleImageUploadForQuestion = async (questionId, file) => {
    if (!file) return;
    
    try {
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'compressing' }));
      
      // ✅ Compress image before uploading
      const compressedFile = await compressImage(file, 1200, 1200, 0.8);
      console.log(`Compressed ${file.name} from ${(file.size / 1024).toFixed(1)}KB to ${(compressedFile.size / 1024).toFixed(1)}KB`);

      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'uploading' }));
      
      const formData = new FormData();
      formData.append('imageFile', compressedFile);
      formData.append('questionId', questionId);

      const res = await fetch(`${API_BASE}/admin/upload/science-question-image`, { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'success' }));
    } catch (err) {
      console.error(err);
      setImageUploadStatus(prev => ({ ...prev, [questionId]: 'error' }));
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🔬 Upload Science Questions</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {status.message && (
        <div style={{ padding: 12, background: status.type === 'success' ? '#d4edda' : '#f8d7da', color: status.type === 'success' ? '#155724' : '#721c24', borderRadius: 6, marginBottom: 15 }}>
          <strong>{status.message}</strong>
          {status.details?.errors?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: 8 }}>
              <strong>Errors:</strong>
              <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                {status.details.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>📄 Step 1: Upload CSV</h3>
        <form onSubmit={handleUploadCSV}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 15, marginBottom: 15 }}>
            <div>
              <label style={labelStyle}>Target Level *</label>
              <select style={inputStyle} value={level} onChange={e => setLevel(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (i + 1).toString()).map(l => <option key={l} value={l}>Level {l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>CSV File *</label>
              <input type="file" accept=".csv" onChange={handleFileChange} style={{ ...inputStyle, padding: 8 }} ref={fileInputRef} />
            </div>
          </div>

          <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, fontSize: 13, color: '#555', lineHeight: 1.5, marginBottom: 15 }}>
            <strong>Required CSV Columns:</strong> <code>chapter, chapter_name, concept, sub_concept, concept_type, question_text, answer_a/b/c/d, correct_answer</code><br/>
            <strong>Optional Columns:</strong> <code>skillset_tag_1/2/3, answer_X_tag1/2/3, needs_image, image_label</code>
          </div>

          <button type="submit" disabled={uploading || !file} style={{ ...btnPrimary, opacity: (uploading || !file) ? 0.6 : 1 }}>
            {uploading ? '⏳ Processing...' : '🚀 Upload Questions'}
          </button>
        </form>
      </div>

      {questionsNeedingImages.length > 0 && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '2px solid #007BFF', marginBottom: 20 }}>
          <h3 style={{ marginTop: 0, color: '#007BFF' }}>📸 Step 2: Attach Images to Questions</h3>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
            The system detected <strong>{questionsNeedingImages.length}</strong> questions that need images. Images will be automatically compressed before uploading.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            {questionsNeedingImages.map((q) => {
              const status = imageUploadStatus[q.id];
              return (
                <div key={q.id} style={{ padding: 15, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                  <div style={{ marginBottom: 10, fontSize: 15 }}>
                    <strong>Question:</strong> {q.question_text.length > 80 ? q.question_text.substring(0, 80) + '...' : q.question_text}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={(e) => handleImageUploadForQuestion(q.id, e.target.files[0])}
                      disabled={status === 'success' || status === 'uploading' || status === 'compressing'}
                      style={{ flex: 1 }}
                    />
                    
                    {status === 'compressing' && <span style={{ color: '#fd7e14', fontWeight: 600 }}>⚙️ Compressing...</span>}
                    {status === 'uploading' && <span style={{ color: '#007BFF', fontWeight: 600 }}>⏳ Uploading...</span>}
                    {status === 'success' && <span style={{ color: '#28a745', fontWeight: 600 }}>✅ Attached!</span>}
                    {status === 'error' && <span style={{ color: '#dc3545', fontWeight: 600 }}>❌ Failed</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 20, padding: 15, background: '#e7f1ff', borderRadius: 6, textAlign: 'center' }}>
            <button 
              onClick={() => { setQuestionsNeedingImages([]); setFile(null); }} 
              style={{ ...btnPrimary, background: '#007BFF' }}
            >
              ✅ All Done! Finish Upload
            </button>
          </div>
        </div>
      )}
    </div>
  );
}